import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faPlus,
  faTrash,
  faCopy,
  faPen,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import {
  useDashboardList,
  useCreateDashboard,
  useDeleteDashboard,
  useCloneDashboard,
} from './hooks/useDashboards';
import styles from './DashboardListPage.module.css';

export default function DashboardListPage() {
  const navigate = useNavigate();
  const { data: dashboards, isLoading } = useDashboardList();
  const createMutation = useCreateDashboard();
  const deleteMutation = useDeleteDashboard();
  const cloneMutation = useCloneDashboard();
  const [newName, setNewName] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const handleCreate = useCallback(() => {
    if (!newName.trim()) return;
    createMutation.mutate(
      { name: newName.trim() },
      {
        onSuccess: (data) => {
          setNewName('');
          setShowCreate(false);
          navigate(`/bi/${data.slug}`);
        },
      },
    );
  }, [newName, createMutation, navigate]);

  const handleDelete = useCallback(
    (id: number, name: string) => {
      if (!window.confirm(`Archive dashboard "${name}"?`)) return;
      deleteMutation.mutate({ id });
    },
    [deleteMutation],
  );

  const handleClone = useCallback(
    (id: number) => {
      cloneMutation.mutate(
        { id },
        {
          onSuccess: (data) => navigate(`/bi/${data.slug}`),
        },
      );
    },
    [cloneMutation, navigate],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleRow}>
          <FontAwesomeIcon icon={faChartBar} className={styles.icon} />
          <h1>Dashboards</h1>
        </div>
        <button className={styles.createBtn} onClick={() => setShowCreate(true)}>
          <FontAwesomeIcon icon={faPlus} /> New Dashboard
        </button>
      </header>

      {showCreate && (
        <div className={styles.createForm}>
          <input
            className={styles.nameInput}
            placeholder="Dashboard name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          <button
            className={styles.createSubmit}
            onClick={handleCreate}
            disabled={!newName.trim() || createMutation.isPending}
          >
            Create
          </button>
          <button className={styles.cancelBtn} onClick={() => setShowCreate(false)}>
            Cancel
          </button>
        </div>
      )}

      {isLoading ? (
        <div className={styles.loading}>Loading dashboards…</div>
      ) : !dashboards?.data?.length ? (
        <div className={styles.empty}>
          <FontAwesomeIcon icon={faChartBar} className={styles.emptyIcon} />
          <h2>No dashboards yet</h2>
          <p>Create your first dashboard to get started.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {dashboards.data.map((d) => (
            <div key={d.id} className={styles.card} onClick={() => navigate(`/bi/${d.slug}`)}>
              <div className={styles.cardHeader}>
                <h3>{d.name}</h3>
                {d.user_permission && (
                  <span className={styles.permBadge}>
                    <FontAwesomeIcon icon={d.user_permission === 'edit' ? faPen : faEye} />
                    {d.user_permission}
                  </span>
                )}
              </div>
              <div className={styles.cardMeta}>
                <span>Updated {new Date(d.updated_at).toLocaleDateString()}</span>
              </div>
              <div className={styles.cardActions} onClick={(e) => e.stopPropagation()}>
                <button title="Clone" onClick={() => handleClone(d.id)}>
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <button title="Archive" onClick={() => handleDelete(d.id, d.name)}>
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
